import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@clerk/react';
import { getBusinesses, type Business } from '@/lib/api';

interface BusinessContextValue {
  businesses: Business[];
  loading: boolean;
  error: string | null;
  activeBusinessId: string | null;
  activeBusiness: Business | null;
  setActiveBusinessId: (id: string) => void;
  refresh: () => Promise<void>;
  addBusiness: (business: Business) => void;
}

const BusinessContext = createContext<BusinessContextValue | null>(null);

const ACTIVE_KEY = 'wanentry.activeBusinessId';

/**
 * Loads the signed-in user's businesses once and shares them across the app.
 * Also tracks which business is "active" (persisted to localStorage for 5d's
 * switcher). Fetches only when Clerk reports a signed-in user.
 */
export function BusinessProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeBusinessId, setActiveIdState] = useState<string | null>(() =>
    window.localStorage.getItem(ACTIVE_KEY),
  );

  const setActiveBusinessId = useCallback((id: string) => {
    setActiveIdState(id);
    window.localStorage.setItem(ACTIVE_KEY, id);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const list = await getBusinesses(token);
      setBusinesses(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load your businesses.');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  const addBusiness = useCallback((business: Business) => {
    setBusinesses((prev) => [...prev, business]);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn) {
      setBusinesses([]);
      setError(null);
      setLoading(false);
      return;
    }
    void refresh();
  }, [isLoaded, isSignedIn, refresh]);

  // Keep the active id pointing at a real business; default to the first.
  useEffect(() => {
    if (businesses.length === 0) return;
    const stillValid = activeBusinessId && businesses.some((b) => b.id === activeBusinessId);
    if (!stillValid) setActiveBusinessId(businesses[0].id);
  }, [businesses, activeBusinessId, setActiveBusinessId]);

  const value = useMemo<BusinessContextValue>(() => {
    const activeBusiness = businesses.find((b) => b.id === activeBusinessId) ?? null;
    return {
      businesses,
      loading,
      error,
      activeBusinessId,
      activeBusiness,
      setActiveBusinessId,
      refresh,
      addBusiness,
    };
  }, [businesses, loading, error, activeBusinessId, setActiveBusinessId, refresh, addBusiness]);

  return <BusinessContext.Provider value={value}>{children}</BusinessContext.Provider>;
}

export function useBusinesses(): BusinessContextValue {
  const ctx = useContext(BusinessContext);
  if (!ctx) {
    throw new Error('useBusinesses must be used within a BusinessProvider');
  }
  return ctx;
}
